import React, { useMemo, useState } from 'react'
import superagent from 'superagent'
import * as moment from 'moment-timezone'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { toast, ToastContainer } from 'react-toastify'

import Breadcrumbs from '@components/Common/Breadcrumb'
import useQuery from '@helpers/JIFI/useQuery'
import { backendUrl } from '@helpers/consts'
import { Card, CardContent as CardBody } from '@shadcn/components/ui/card'
import { Button } from '@shadcn/components/ui/button'
import { Input } from '@shadcn/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shadcn/components/ui/select'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@shadcn/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableSkeleton } from '@shadcn/components/ui/table'
import { TablePagination } from '@components/supply-chain/TablePagination'

const balanceColumnHelper = createColumnHelper()
const movementColumnHelper = createColumnHelper()

const FILTER_ALL = '__ALL__'

const formatDate = (date) => (date ? moment.tz(date, 'Australia/Melbourne').format('D MMM, h:mm a z') : '-')
const formatQuantity = (value) => Number(value || 0).toLocaleString()
const getErrorMessage = (error, fallback) => error?.response?.body?.message || error?.message || fallback
const BalanceSkuLabel = ({ balance }) => (
  <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
    <span><span className="uppercase font-medium">Location</span> <span className="font-semibold text-black">{balance?.locationCode || '-'}</span></span>
    <span className="text-gray-300">|</span>
    <span><span className="uppercase font-medium">SKU</span> <span className="font-mono font-semibold text-black">{balance?.sku || '-'}</span></span>
  </div>
)

const DualInventoryPage = () => {
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(50)
  const [movementPage, setMovementPage] = useState(1)
  const [movementPerPage, setMovementPerPage] = useState(10)
  const [refresh, setRefresh] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterProvider, setFilterProvider] = useState(FILTER_ALL)
  const [filterLocationCode, setFilterLocationCode] = useState(FILTER_ALL)
  const [varianceOnly, setVarianceOnly] = useState(false)
  const [selectedBalance, setSelectedBalance] = useState(null)
  const [adjustmentBalance, setAdjustmentBalance] = useState(null)
  const [balancePendingReset, setBalancePendingReset] = useState(null)
  const [adjustmentDelta, setAdjustmentDelta] = useState('')
  const [adjustmentMessage, setAdjustmentMessage] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const queryData = useMemo(
    () => ({
      search: searchTerm || undefined,
      provider: filterProvider === FILTER_ALL ? undefined : filterProvider,
      locationCode: filterLocationCode === FILTER_ALL ? undefined : filterLocationCode,
      varianceOnly: varianceOnly || undefined,
      page,
      limit: perPage,
    }),
    [searchTerm, filterProvider, filterLocationCode, varianceOnly, page, perPage],
  )

  const [locationsResp] = useQuery({ url: '/inventory/getLocations' })

  const externalLocations = useMemo(() => {
    const list = Array.isArray(locationsResp)
      ? locationsResp
      : Array.isArray(locationsResp?.data)
        ? locationsResp.data
        : []
    return list.filter((loc) => loc?.logistics_provider && loc?.code)
  }, [locationsResp])

  const providerOptions = useMemo(() => {
    const set = new Set(externalLocations.map((l) => l.logistics_provider).filter(Boolean))
    return [...set].sort((a, b) => String(a).localeCompare(String(b)))
  }, [externalLocations])

  const locationFilterOptions = useMemo(() => {
    let list = externalLocations
    if (filterProvider !== FILTER_ALL) {
      list = list.filter((l) => l.logistics_provider === filterProvider)
    }
    return [...list].sort((a, b) =>
      String(a.display_name || a.name || '').localeCompare(String(b.display_name || b.name || '')),
    )
  }, [externalLocations, filterProvider])

  const [reconciliationResp, reconciliationState] = useQuery({
    url: '/inventory/dual-inventory/reconciliation',
    queryData,
    refresh,
  })

  const [movementResp, movementState] = useQuery({
    url: '/inventory/dual-inventory/movements',
    queryData: {
      locationCode: selectedBalance?.locationCode,
      sku: selectedBalance?.sku ? encodeURIComponent(selectedBalance.sku) : undefined,
      page: movementPage,
      limit: movementPerPage,
    },
    refresh,
    runQuery: Boolean(selectedBalance?.locationCode && selectedBalance?.sku),
  })

  const reconciliationData = reconciliationResp?.data || []
  const reconciliationCount = reconciliationResp?.count || 0
  const movementData = movementResp?.data || []
  const movementCount = movementResp?.count || 0
  const adjustmentDeltaNumber = Number(adjustmentDelta)
  const canPreviewAdjustment = adjustmentBalance && Number.isFinite(adjustmentDeltaNumber) && adjustmentDelta !== ''
  const currentExpected = Number(adjustmentBalance?.jifiExpectedOnHand || 0)
  const currentActual = Number(adjustmentBalance?.providerActualOnHand || 0)
  const previewExpected = canPreviewAdjustment ? currentExpected + adjustmentDeltaNumber : currentExpected
  const previewVariance = previewExpected - currentActual

  const refreshData = () => setRefresh((current) => current + 1)

  const openAdjustment = (balance) => {
    setAdjustmentBalance(balance)
    setAdjustmentDelta('')
    setAdjustmentMessage('')
  }

  const postDualInventoryAction = async (path, body) => {
    return superagent
      .post(`${backendUrl}${path}`)
      .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
      .send(body)
  }

  const submitAdjustment = async () => {
    const quantityDelta = Number(adjustmentDelta)
    if (!adjustmentBalance || !Number.isFinite(quantityDelta) || quantityDelta === 0) {
      toast.error('Quantity delta must be a non-zero number')
      return
    }

    setIsSaving(true)
    try {
      await postDualInventoryAction('/inventory/dual-inventory/manual-adjustment', {
        locationCode: adjustmentBalance.locationCode,
        sku: adjustmentBalance.sku,
        quantityDelta,
        message: adjustmentMessage || undefined,
      })
      toast.success('Dual inventory adjustment saved')
      refreshData()
    } catch (error) {
      console.error(error)
      toast.error(getErrorMessage(error, 'Failed to save manual adjustment'))
    } finally {
      setIsSaving(false)
      setAdjustmentBalance(null)
    }
  }

  const confirmResetToActual = async () => {
    if (!balancePendingReset) return

    setIsSaving(true)
    try {
      await postDualInventoryAction('/inventory/dual-inventory/reset', {
        locationCode: balancePendingReset.locationCode,
        sku: balancePendingReset.sku,
      })
      toast.success('JIFI expected count reset to provider actual')
      refreshData()
    } catch (error) {
      console.error(error)
      toast.error(getErrorMessage(error, 'Failed to reset dual inventory'))
    } finally {
      setIsSaving(false)
      setBalancePendingReset(null)
    }
  }

  const balanceColumns = useMemo(
    () => [
      balanceColumnHelper.accessor('provider', {
        header: () => 'Provider',
        cell: ({ row }) => <span className="block w-[90px] truncate">{row.original.provider}</span>,
      }),
      balanceColumnHelper.accessor('locationCode', {
        header: () => 'Location',
        cell: ({ row }) => (
          <div>
            <div className="font-medium text-black">{row.original.locationCode}</div>
            {row.original.locationName && <div className="text-xs text-gray-500">{row.original.locationName}</div>}
          </div>
        ),
      }),
      balanceColumnHelper.accessor('sku', {
        header: () => 'SKU',
        cell: ({ row }) => <span className="block min-w-[170px] whitespace-nowrap font-mono text-black">{row.original.sku}</span>,
      }),
      balanceColumnHelper.accessor('jifiExpectedOnHand', {
        header: () => 'JIFI Expected',
        cell: ({ row }) => formatQuantity(row.original.jifiExpectedOnHand),
      }),
      balanceColumnHelper.accessor('providerActualOnHand', {
        header: () => 'Provider Actual',
        cell: ({ row }) => formatQuantity(row.original.providerActualOnHand),
      }),
      balanceColumnHelper.accessor('variance', {
        header: () => 'Variance',
        cell: ({ row }) => {
          const variance = Number(row.original.variance || 0)
          const className = variance === 0 ? 'text-gray-500' : variance > 0 ? 'text-amber-700' : 'text-red-700'
          return <span className={`font-medium ${className}`}>{formatQuantity(variance)}</span>
        },
      }),
      balanceColumnHelper.accessor('lastProviderSyncAt', {
        header: () => 'Last Provider Sync',
        cell: ({ row }) => formatDate(row.original.lastProviderSyncAt),
      }),
      balanceColumnHelper.accessor('lastJifiMovementAt', {
        header: () => 'Last JIFI Movement',
        cell: ({ row }) => formatDate(row.original.lastJifiMovementAt),
      }),
      balanceColumnHelper.display({
        id: 'actions',
        header: () => 'Actions',
        cell: ({ row }) => (
          <div className="flex flex-nowrap items-center gap-2 min-w-[250px]">
            <Button
              variant="outline"
              size="sm"
              className="whitespace-nowrap px-2"
              onClick={() => {
                setMovementPage(1)
                setSelectedBalance(row.original)
              }}
            >
              View Movements
            </Button>
            <Button variant="outline" size="sm" className="whitespace-nowrap px-2" onClick={() => openAdjustment(row.original)}>
              Adjust
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="whitespace-nowrap px-2"
              disabled={isSaving}
              onClick={() => setBalancePendingReset(row.original)}
            >
              Reset
            </Button>
          </div>
        ),
      }),
    ],
    [isSaving],
  )

  const movementColumns = useMemo(
    () => [
      movementColumnHelper.accessor('createdAt', {
        header: () => 'Date',
        cell: ({ row }) => formatDate(row.original.createdAt),
      }),
      movementColumnHelper.accessor('movementType', {
        header: () => 'Movement Type',
      }),
      movementColumnHelper.accessor('quantityDelta', {
        header: () => 'Delta',
        cell: ({ row }) => formatQuantity(row.original.quantityDelta),
      }),
      movementColumnHelper.accessor('quantityBefore', {
        header: () => 'Before',
        cell: ({ row }) => formatQuantity(row.original.quantityBefore),
      }),
      movementColumnHelper.accessor('quantityAfter', {
        header: () => 'After',
        cell: ({ row }) => formatQuantity(row.original.quantityAfter),
      }),
      movementColumnHelper.accessor('referenceNumber', {
        header: () => 'Reference',
        cell: ({ row }) => row.original.referenceNumber || row.original.referenceId || '-',
      }),
      movementColumnHelper.accessor('userName', {
        header: () => 'User',
        cell: ({ row }) => row.original.userName || '-',
      }),
      movementColumnHelper.accessor('message', {
        header: () => 'Message',
        cell: ({ row }) => row.original.message || '-',
      }),
    ],
    [],
  )

  const balanceTable = useReactTable({
    data: reconciliationData,
    columns: balanceColumns,
    getCoreRowModel: getCoreRowModel(),
  })
  const movementTable = useReactTable({
    data: movementData,
    columns: movementColumns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="page-content">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="w-full px-3 mx-auto xl:max-w-[95%] pt-4">
        <Breadcrumbs title="Inventory" breadcrumbItem="Dual Inventory System" />

        <Card className="mb-6 pt-6">
          <CardBody>
            <div
              className="mb-4 grid items-start gap-4"
              style={{ gridTemplateColumns: '420px 280px 320px 160px' }}
            >
              <div>
                <label className="control-label text-uppercase d-block mb-2">Search</label>
                <div className="position-relative">
                  <Input
                    type="text"
                    className="form-control h-11 w-full rounded-md border border-black bg-white px-3 py-2 text-black"
                    placeholder="Search provider, location, or SKU..."
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.keyCode === 13) {
                        setPage(1)
                        setSearchTerm(event.target.value)
                      }
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="control-label text-uppercase d-block mb-2">Provider</label>
                <Select
                  value={filterProvider}
                  onValueChange={(value) => {
                    setPage(1)
                    setFilterProvider(value)
                    setFilterLocationCode(FILTER_ALL)
                  }}
                >
                  <SelectTrigger className="h-11 w-full rounded-md border border-black bg-white px-3 py-2 text-black">
                    <SelectValue placeholder="All providers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={FILTER_ALL}>All providers</SelectItem>
                    {providerOptions.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="control-label text-uppercase d-block mb-2">Location</label>
                <Select
                  value={filterLocationCode}
                  onValueChange={(value) => {
                    setPage(1)
                    setFilterLocationCode(value)
                  }}
                >
                  <SelectTrigger className="h-11 w-full rounded-md border border-black bg-white px-3 py-2 text-black">
                    <SelectValue placeholder="All locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={FILTER_ALL}>All locations</SelectItem>
                    {locationFilterOptions.map((loc) => (
                      <SelectItem key={loc.code} value={loc.code}>
                        {loc.display_name || loc.name || loc.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="d-block mb-2" style={{ height: 18 }} />
                <div className="d-flex h-11 align-items-center">
                  <label className="d-flex align-items-center gap-2 cursor-pointer whitespace-nowrap m-0">
                    <input
                      type="checkbox"
                      checked={varianceOnly}
                      onChange={(event) => {
                        setPage(1)
                        setVarianceOnly(event.target.checked)
                      }}
                    />
                    <span className="text-sm text-black">Variance only</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="table-responsive rounded-lg border border-gray-200 bg-white text-black">
              <Table className="table-auto w-full">
                <TableHeader>
                  {balanceTable.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id} className="bg-black hover:bg-black">
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id} className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                {reconciliationState === 'loading' && reconciliationData.length === 0 ? (
                  <TableSkeleton columns={9} perPage={perPage} />
                ) : (
                  <TableBody>
                    {balanceTable.getRowModel().rows?.length ? (
                      balanceTable.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          className={Number(row.original.variance || 0) !== 0 ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id} className="px-4 py-3 text-black">
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={9} className="px-4 py-6 text-center text-black">
                          No dual inventory records found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                )}
              </Table>
            </div>

            {reconciliationCount > perPage && (
              <div className="mt-4">
                <TablePagination count={reconciliationCount} page={page} setPage={setPage} perPage={perPage} setPerPage={setPerPage} />
              </div>
            )}
          </CardBody>
        </Card>

        <Dialog open={Boolean(selectedBalance)} onOpenChange={(open) => !open && setSelectedBalance(null)}>
          <DialogContent className="max-w-[95vw] w-[95vw] max-h-[85vh] overflow-y-auto bg-white text-black">
            <DialogHeader>
              <DialogTitle>Expected Movement History</DialogTitle>
              <BalanceSkuLabel balance={selectedBalance} />
            </DialogHeader>
              <div className="table-responsive rounded-lg border border-gray-200 bg-white text-black">
                <Table className="table-auto w-full">
                  <TableHeader>
                    {movementTable.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id} className="bg-black hover:bg-black">
                        {headerGroup.headers.map((header) => (
                          <TableHead key={header.id} className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  {movementState === 'loading' ? (
                    <TableSkeleton columns={8} perPage={movementPerPage} />
                  ) : (
                    <TableBody>
                      {movementTable.getRowModel().rows?.length ? (
                        movementTable.getRowModel().rows.map((row) => (
                          <TableRow key={row.id} className="hover:bg-gray-50">
                            {row.getVisibleCells().map((cell) => (
                              <TableCell key={cell.id} className="px-4 py-3 text-black">
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} className="px-4 py-6 text-center text-black">
                            No expected movements found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  )}
                </Table>
              </div>
              {movementCount > movementPerPage && (
                <div className="mt-4">
                  <TablePagination
                    count={movementCount}
                    page={movementPage}
                    setPage={setMovementPage}
                    perPage={movementPerPage}
                    setPerPage={setMovementPerPage}
                  />
                </div>
              )}
          </DialogContent>
        </Dialog>

        <Dialog open={Boolean(adjustmentBalance)} onOpenChange={(open) => !open && setAdjustmentBalance(null)}>
          <DialogContent className="sm:max-w-[520px] bg-white text-black">
            <DialogHeader>
              <DialogTitle>Manual Adjustment</DialogTitle>
              <BalanceSkuLabel balance={adjustmentBalance} />
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="block text-sm text-black mb-1">JIFI Expected Delta</label>
                <Input
                  type="number"
                  value={adjustmentDelta}
                  onChange={(event) => setAdjustmentDelta(event.target.value)}
                  placeholder="Example: -1 or 1"
                />
                <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                  <div>
                    JIFI Expected: {formatQuantity(currentExpected)}
                    {canPreviewAdjustment ? ` + (${formatQuantity(adjustmentDeltaNumber)}) = ${formatQuantity(previewExpected)}` : ''}
                  </div>
                  <div>Provider Actual: {formatQuantity(currentActual)} (unchanged)</div>
                  <div>New Variance: {canPreviewAdjustment ? formatQuantity(previewVariance) : formatQuantity(currentExpected - currentActual)}</div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Manual adjustment changes JIFI Expected only. Provider Actual still comes from provider sync.
                </div>
              </div>
              <div>
                <label className="block text-sm text-black mb-1">Message</label>
                <Input value={adjustmentMessage} onChange={(event) => setAdjustmentMessage(event.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAdjustmentBalance(null)}>Cancel</Button>
              <Button type="button" disabled={isSaving} onClick={submitAdjustment}>{isSaving ? 'Saving...' : 'Save'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={Boolean(balancePendingReset)} onOpenChange={(open) => !open && setBalancePendingReset(null)}>
          <DialogContent className="sm:max-w-[440px] bg-white text-black">
            <DialogHeader>
              <DialogTitle>Reset JIFI expected count?</DialogTitle>
              <BalanceSkuLabel balance={balancePendingReset} />
            </DialogHeader>
            <p className="text-sm text-gray-500">Set JIFI expected count to provider actual for this SKU. A reset movement will be recorded.</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBalancePendingReset(null)}>Cancel</Button>
              <Button type="button" variant="destructive" disabled={isSaving} onClick={confirmResetToActual}>
                {isSaving ? 'Resetting...' : 'Reset'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}

export default DualInventoryPage
